import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NbAuthModule, NbAuthSocialLink, NbPasswordAuthStrategy, NbDummyAuthStrategy } from '@nebular/auth';
import { NbSecurityModule, NbRoleProvider } from '@nebular/security';
import { of as observableOf } from 'rxjs';

import { throwIfAlreadyLoaded } from './module-import-guard';
import {
  AnalyticsService,
  LayoutService,
  PlayerService,
  SeoService,
  StateService,
} from './utils';
import { UserData } from './data/users';
import { ElectricityData } from './data/electricity';
import { SmartTableData } from './data/smart-table';
import { UserActivityData } from './data/user-activity';
import { OrdersChartData } from './data/orders-chart';
import { ProfitChartData } from './data/profit-chart';
import { TrafficListData } from './data/traffic-list';
import { EarningData } from './data/earning';
import { OrdersProfitChartData } from './data/orders-profit-chart';
import { TrafficBarData } from './data/traffic-bar';
import { ProfitBarAnimationChartData } from './data/profit-bar-animation-chart';
import { TemperatureHumidityData } from './data/temperature-humidity';
import { SolarData } from './data/solar';
import { TrafficChartData } from './data/traffic-chart';
import { StatsBarData } from './data/stats-bar';
import { CountryOrderData } from './data/country-order';
import { StatsProgressBarData } from './data/stats-progress-bar';
import { VisitorsAnalyticsData } from './data/visitors-analytics';
import { SecurityCamerasData } from './data/security-cameras';

import { UserService } from './mock/users.service';
import { ElectricityService } from './mock/electricity.service';
import { SmartTableService } from './mock/smart-table.service';
import { UserActivityService } from './mock/user-activity.service';
import { OrdersChartService } from './mock/orders-chart.service';
import { ProfitChartService } from './mock/profit-chart.service';
import { TrafficListService } from './mock/traffic-list.service';
import { EarningService } from './mock/earning.service';
import { OrdersProfitChartService } from './mock/orders-profit-chart.service';
import { TrafficBarService } from './mock/traffic-bar.service';
import { ProfitBarAnimationChartService } from './mock/profit-bar-animation-chart.service';
import { TemperatureHumidityService } from './mock/temperature-humidity.service';
import { SolarService } from './mock/solar.service';
import { TrafficChartService } from './mock/traffic-chart.service';
import { StatsBarService } from './mock/stats-bar.service';
import { CountryOrderService } from './mock/country-order.service';
import { StatsProgressBarService } from './mock/stats-progress-bar.service';
import { VisitorsAnalyticsService } from './mock/visitors-analytics.service';
import { SecurityCamerasService } from './mock/security-cameras.service';
import { MockDataModule } from './mock/mock-data.module';

const socialLinks: NbAuthSocialLink[] = [];

const DATA_SERVICES = [
  { provide: UserData, useClass: UserService },
  { provide: ElectricityData, useClass: ElectricityService },
  { provide: SmartTableData, useClass: SmartTableService },
  { provide: UserActivityData, useClass: UserActivityService },
  { provide: OrdersChartData, useClass: OrdersChartService },
  { provide: ProfitChartData, useClass: ProfitChartService },
  { provide: TrafficListData, useClass: TrafficListService },
  { provide: EarningData, useClass: EarningService },
  { provide: OrdersProfitChartData, useClass: OrdersProfitChartService },
  { provide: TrafficBarData, useClass: TrafficBarService },
  { provide: ProfitBarAnimationChartData, useClass: ProfitBarAnimationChartService },
  { provide: TemperatureHumidityData, useClass: TemperatureHumidityService },
  { provide: SolarData, useClass: SolarService },
  { provide: TrafficChartData, useClass: TrafficChartService },
  { provide: StatsBarData, useClass: StatsBarService },
  { provide: CountryOrderData, useClass: CountryOrderService },
  { provide: StatsProgressBarData, useClass: StatsProgressBarService },
  { provide: VisitorsAnalyticsData, useClass: VisitorsAnalyticsService },
  { provide: SecurityCamerasData, useClass: SecurityCamerasService },
];

export class NbSimpleRoleProvider extends NbRoleProvider {
  getRole() {
    // here you could provide any role based on any auth flow
    return observableOf('guest');
  }
}

const firebaseConfig = {
  endpoint: 'https://identitytoolkit.googleapis.com/v1/accounts:signInWithCustomToken?key=',
  apiKey: 'AIzaSyCxRKgu5Gbdzz368N1Q6jp4IQAHn0j-hZ4',
  authDomain: 'fir-tramites.firebaseapp.com',
  databaseURL: 'https://fir-tramites.firebaseio.com',
  projectId: 'fir-tramites',
  storageBucket: 'fir-tramites.appspot.com',
  messagingSenderId: '310312340316',
  appId: '1:310312340316:web:5e512b89f6886330250f99',
  measurementId: 'G-B90487G31V',
};

export const NB_CORE_PROVIDERS = [
  ...MockDataModule.forRoot().providers,
  ...DATA_SERVICES,
  ...NbAuthModule.forRoot({

    strategies: [

      /*
      NbPasswordAuthStrategy.setup({
        name: 'email',
        firebaseConfig: firebaseConfig,
        login: {
          defaultErrors: ['La combinación de inicio de sesión / correo electrónico no es correcta, intente nuevamente.'],
          defaultMessages: ['Has ingresado exitosamente.'],
        },
        register: {
          defaultErrors: ['Algo salió mal. Por favor, vuelva a intentarlo.'],
          defaultMessages: ['Te has registrado con éxito.'],
        },
        requestPass: {
          defaultErrors: ['Algo salió mal. Por favor, vuelva a intentarlo.'],
          defaultMessages: ['Las instrucciones para restablecer la contraseña se han enviado a su correo electrónico.'],
        },
        resetPass: {
          defaultErrors: ['Algo salió mal. Por favor, vuelva a intentarlo.'],
          defaultMessages: ['Su contraseña ha sido cambiada exitosamente.'],
        },
        logout: {
          defaultErrors: ['Algo salió mal. Por favor, vuelva a intentarlo.'],
          defaultMessages: ['Ha sido desconectado exitosamente.'],
        },
      }),
      */
      NbDummyAuthStrategy.setup({
        name: 'email',
        delay: 3000,
      }),
    ],
    forms: {
      login: {
        redirectDelay: 500,
        strategy: 'email',
        rememberMe: true,
        showMessages: {
          success: true,
          error: true,
        },
        socialLinks: socialLinks,
      },
      register: {
        socialLinks: socialLinks,
      },
      requestpass: {
        strategy: 'email',
        showMessages: {
          success: true,
          error: true,
        },
        socialLinks: socialLinks,
      },
      validation: {
        password: {
          required: true,
          minLength: 8,
          maxLength: 42,
        },
        email: {
          required: true,
        },
        firstName: {
          required: true,
          minLength: 4,
          maxLenght: 50,
        },
        lastName: {
          required: true,
          minLength: 4,
          maxLenght: 50,
        },
        documentType: {
          required: true,
        },
        document: {
          required: true,
          minLength: 6,
          maxLenght: 12,
        },
        phone: {
          required: true,
          minLength: 7,
          maxLenght: 10,
        },
        cellularPhone: {
          required: true,
          minLength: 10,
          maxLenght: 10,
        },
        state: {
          required: true,
        },
        city: {
          required: true,
        },
        zipCode: {
          required: false,
          minLength: 5,
          maxLenght: 6,
        },
        address: {
          required: true,
          minLength: 8,
          maxLenght: 50,
        },
        addressInfo: {
          required: false,
        },
        requestForm: {
          requestGroup: {
            required: true,
          },
          plate: {
            required: true,
            minLength: 5,
            maxLenght: 8,
          },
          requesterType: {
            required: true,
          },
          mainPerson_firstName: {
            required: true,
            minLength: 4,
            maxLenght: 50,
          },
          mainPerson_lastName: {
            required: true,
            minLength: 4,
            maxLenght: 50,
          },
          mainPerson_documentType: {
            required: true,
          },
          mainPerson_document: {
            required: true,
            minLength: 6,
            maxLenght: 12,
          },
          mainPerson_phone: {
            required: true,
            minLength: 7,
            maxLenght: 10,
          },
          mainPerson_cellularPhone: {
            required: true,
            minLength: 10,
            maxLenght: 10,
          },
          mainPerson_state: {
            required: true,
          },
          mainPerson_city: {
            required: true,
          },
          mainPerson_zipCode: {
            required: false,
            minLength: 5,
            maxLenght: 6,
          },
          mainPerson_address: {
            required: true,
            minLength: 8,
            maxLenght: 50,
          },
          mainPerson_addressInfo: {
            required: false,
          },
        },
      },
    },
  }).providers,

  NbSecurityModule.forRoot({
    accessControl: {
      guest: {
        view: '*',
      },
      user: {
        parent: 'guest',
        create: '*',
        edit: '*',
        remove: '*',
      },
    },
  }).providers,

  {
    provide: NbRoleProvider, useClass: NbSimpleRoleProvider,
  },
  AnalyticsService,
  LayoutService,
  PlayerService,
  SeoService,
  StateService,
];

@NgModule({
  imports: [
    CommonModule,
  ],
  exports: [
    NbAuthModule,
  ],
  declarations: [],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }

  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        ...NB_CORE_PROVIDERS,
      ],
    };
  }
}
